import {
    getErrorMessage,
    translateKeyWithDetail,
    useMutationWithHandler,
} from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import {
    updateService,
    updateServiceBillingOrganization,
    getServiceMetadata,
    getServiceUUIDAssignmentOptions,
    updateServiceUUIDAssignment,
} from '@/modules/Service/services'
import { Service } from '@/modules/Service/types'
import {
    DEFAULT_PAYMENT_TERM_DAYS,
    ServiceCreatableType,
} from '@/modules/Service/constants'
import CustomerSelect from '@/components/Form/Shared/CustomerSelect'
import { ModalProps } from '@/types/shared'
import { formatPGDate, pick } from '@/utils/function'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { Dialog } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    DatacentralHookForm,
    type DatacentralHookFormDataItem,
} from '@/components/ui/form-field'
import type { EndDatePreset } from '@/components/ui/date-picker/date-range-picker'

const MAX_PAYMENT_TERM_DAYS = 365

const SERVICE_CYCLE_PRESETS: EndDatePreset[] = [
    {
        name: '1month',
        label: '1 tháng',
        getEndDate: (s) => s.add(1, 'month').subtract(1, 'day').toDate(),
    },
    {
        name: '3months',
        label: '3 tháng',
        getEndDate: (s) => s.add(3, 'month').subtract(1, 'day').toDate(),
    },
    {
        name: '6months',
        label: '6 tháng',
        getEndDate: (s) => s.add(6, 'month').subtract(1, 'day').toDate(),
    },
    {
        name: '1year',
        label: '1 năm',
        getEndDate: (s) => s.add(1, 'year').subtract(1, 'day').toDate(),
    },
    {
        name: '2years',
        label: '2 năm',
        getEndDate: (s) => s.add(2, 'year').subtract(1, 'day').toDate(),
    },
]

export interface ServiceEditProps extends ModalProps {
    serviceID: number
    service: Service
}
const ServiceEdit: React.FC<ServiceEditProps> = ({
    open,
    onClose,
    service,
    serviceID,
}) => {
    const { t } = useTranslation()
    const { t: tService } = useTranslation('services')
    const formControl = useForm<Partial<Service>>({
        defaultValues: {
            service_time_id: service?.service_time_id,
            ...pick(service, [
                'service_start_date',
                'first_startdate',
                'first_enddate',
                'current_startdate',
                'current_enddate',
                'continue_startdate',
                'continue_enddate',
                'renew_range',
                'statement_range',
                'statement_startdate',
                'statement_enddate',
            ]),
            payment_term_days:
                service?.payment_term_days ?? DEFAULT_PAYMENT_TERM_DAYS,
        },
    })
    const { refetch } = useQueryClientCallback({
        reactQueryKeys: [
            `services.${serviceID}`,
            `services.updates.${serviceID}`,
        ],
    })

    // Reconcile-scoped templates (CDN + transcode families) share the same two
    // margarita-backed config controls: tổ chức (khách hàng) nguồn số liệu đối soát
    // và dịch vụ @mgmt để lấy số liệu. Mặc định là khách hàng của dịch vụ; có thể
    // trỏ sang khách hàng khác cho các case reseller/indirection (vd VTV bill dưới
    // CMC, 24h). Mọi thay đổi được ghi log ở server (service_updates).
    const isReconcileScoped = [
        'SwiftFederation.CDN',
        'SwiftFederation.Transcoding',
        'SwiftFederation.Transmuxing',
        'SwiftFederation.Recording',
        'SwiftFederation.StorageOSS',
    ].includes(service?.service_template ?? '')
    const { data: billingMetadata, isLoading: isBillingMetaLoading } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.SERVICES_METADATA, serviceID],
            queryFn: () => getServiceMetadata(serviceID),
            enabled: !!serviceID && isReconcileScoped,
        })
    const currentBillingOrg =
        Number(
            (billingMetadata?.metadata as Record<string, any> | undefined)
                ?.billing_organization_id
        ) || service?.customer_id

    // Nạp billing_organization_id vào form NGAY khi metadata load xong, TRƯỚC khi
    // render CustomerSelect. AsyncSelectWithServer chỉ resolve nhãn cho giá trị đã
    // có sẵn lúc mount; nếu set sau mount (qua defaultCustomerID async) thì select
    // đứng trống. Vì vậy phải bơm value trước rồi mới dựng select.
    const billingOrgValue = formControl.watch(
        'billing_organization_id' as never
    )
    useEffect(() => {
        if (
            isReconcileScoped &&
            !isBillingMetaLoading &&
            billingOrgValue == null
        ) {
            formControl.setValue(
                'billing_organization_id' as never,
                currentBillingOrg as never
            )
        }
    }, [
        isReconcileScoped,
        isBillingMetaLoading,
        currentBillingOrg,
        billingOrgValue,
        formControl,
    ])

    const { mutateAsync: saveBillingOrg } = useMutationWithHandler(
        { mutationFn: updateServiceBillingOrganization },
        {
            showSuccessToast: false,
            reactQueryKeys: [
                QUERY_KEYS.SERVICE_DETAIL,
                QUERY_KEYS.SERVICES_METADATA,
            ],
        }
    )

    // CDN margarita-service mapping: re-point service_creatable_uuid -> margarita
    // services.id (marked by service_creatable_type = 'ServiceMgmt') so
    // đối soát/monitoring lấy đúng số của service, không phải cả org. Danh sách lấy từ
    // @mgmt theo org (đã tính cả billing_organization_id override).
    const { data: assignmentOptions } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.SERVICES_METADATA,
            serviceID,
            'service-uuid-assignment',
        ],
        queryFn: () => getServiceUUIDAssignmentOptions(serviceID),
        enabled: !!serviceID && isReconcileScoped,
    })
    // Current assignment = service_creatable_uuid when it has been migrated to a
    // servicemgmt service (creatable_type marker); else empty (org scope).
    const savedAssignedServiceId =
        service?.service_creatable_type === ServiceCreatableType.ServiceMgmt
            ? service?.service_creatable_uuid || ''
            : ''
    // Cảnh báo: creatable_uuid đang gán KHÔNG nằm trong list @mgmt lấy theo org (assignment
    // lệch/stale — vd org override đổi, dịch vụ @mgmt bị xoá/di chuyển). Chỉ báo khi list đã
    // load (là mảng) để tránh báo nhầm lúc đang tải.
    const assignmentOrphaned =
        !!savedAssignedServiceId &&
        Array.isArray(assignmentOptions) &&
        !assignmentOptions.some((s) => s.id === savedAssignedServiceId)
    const [assignedServiceId, setAssignedServiceId] = useState<string>('')
    useEffect(() => {
        setAssignedServiceId(savedAssignedServiceId)
    }, [savedAssignedServiceId])

    const { mutateAsync: saveAssignment } = useMutationWithHandler(
        { mutationFn: updateServiceUUIDAssignment },
        {
            showSuccessToast: false,
            reactQueryKeys: [
                QUERY_KEYS.SERVICE_DETAIL,
                QUERY_KEYS.SERVICES_METADATA,
            ],
        }
    )

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: updateService,
        },
        {
            successMessage: t('update-service-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES,
            // Toast mặc định không tra namespace module, nên tự dịch để lỗi riêng
            // của dịch vụ (vd invalid-payment-term-days) nằm ở services locale.
            showErrorToast: false,
            onError: (error) => {
                const message = getErrorMessage(error)
                const translated = t(message, {
                    ns: ['axios-error', 'services', 'translation'],
                    defaultValue: '',
                })
                toast.error(
                    translated ||
                        translateKeyWithDetail(message, (key, options) =>
                            t(key, { ...options, ns: 'axios-error' })
                        ) ||
                        message
                )
            },
            onSuccess: () => {
                onClose()
                refetch()
            },
        }
    )

    const onSubmit = async (values: Partial<Service>) => {
        const paymentTermDays = values.payment_term_days
        if (
            !Number.isInteger(paymentTermDays) ||
            (paymentTermDays as number) < 0 ||
            (paymentTermDays as number) > MAX_PAYMENT_TERM_DAYS
        ) {
            formControl.setError('payment_term_days', {
                message: 'services:payment_term_days_invalid',
            })
            return
        }

        // Validate: continue_startdate must be after current_enddate
        if (values.continue_startdate && values.current_enddate) {
            if (
                dayjs(values.continue_startdate).isBefore(
                    dayjs(values.current_enddate),
                    'day'
                ) ||
                dayjs(values.continue_startdate).isSame(
                    dayjs(values.current_enddate),
                    'day'
                )
            ) {
                toast.error(
                    tService('continue_startdate_must_be_after_current_enddate')
                )
                return
            }
        }

        // billing_organization_id is service metadata, not a services-table field.
        // Persist it via the dedicated (audited) endpoint and keep it out of the
        // service update payload.
        const { billing_organization_id: billingOrgValue, ...serviceValues } =
            values as Partial<Service> & { billing_organization_id?: number }
        if (isReconcileScoped) {
            await saveBillingOrg({
                serviceID,
                billing_organization_id: billingOrgValue
                    ? String(billingOrgValue)
                    : '',
            })
            // Persist the margarita-service mapping only when it changed (audited).
            if (assignedServiceId !== savedAssignedServiceId) {
                await saveAssignment({
                    serviceID,
                    service_uuid_assignment: assignedServiceId,
                })
            }
        }

        mutateAsync({
            serviceID,
            data: {
                ...serviceValues,
                service_start_date: formatPGDate(
                    values?.service_start_date || '',
                    'YYYY-MM-DD'
                ),
                current_startdate: formatPGDate(
                    values?.current_startdate || '',
                    'YYYY-MM-DD'
                ),
                current_enddate: formatPGDate(
                    values?.current_enddate || '',
                    'YYYY-MM-DD'
                ),
                first_startdate: formatPGDate(
                    values?.first_startdate || '',
                    'YYYY-MM-DD'
                ),
                first_enddate: formatPGDate(
                    values?.first_enddate || '',
                    'YYYY-MM-DD'
                ),
                continue_startdate: formatPGDate(
                    values?.continue_startdate || '',
                    'YYYY-MM-DD'
                ),
                continue_enddate: formatPGDate(
                    values?.continue_enddate || '',
                    'YYYY-MM-DD'
                ),
                statement_startdate: formatPGDate(
                    values?.statement_startdate || '',
                    'YYYY-MM-DD'
                ),
                statement_enddate: formatPGDate(
                    values?.statement_enddate || '',
                    'YYYY-MM-DD'
                ),
            },
        })
    }

    const currentEnddate = formControl.watch('current_enddate')
    const renewRange = formControl.watch('renew_range')
    const renewEstimate = dayjs(currentEnddate).subtract(
        Number(renewRange) || 0,
        'days'
    )

    const formData: DatacentralHookFormDataItem<Partial<Service>>[] = [
        {
            formItemProps: {
                name: 'service_start_date',
                type: 'datepicker',
                label: t('service_start_date'),
                disabled: true,
            },
            colSpan: 4,
        },
        {
            formItemProps: {
                name: ['first_startdate', 'first_enddate'],
                type: 'daterangepicker',
                label: tService('first_date_range'),
                numberOfMonths: 2,
                disabled: true,
            },
            colSpan: 4,
        },
        {
            formItemProps: {
                name: ['current_startdate', 'current_enddate'],
                type: 'daterangepicker',
                label: tService('current_date_range'),
                numberOfMonths: 2,
                endDatePresets: SERVICE_CYCLE_PRESETS,
            },
            colSpan: 4,
        },
        {
            formItemProps: {
                name: ['continue_startdate', 'continue_enddate'],
                type: 'daterangepicker',
                label: tService('continue_date_range'),
                numberOfMonths: 2,
                endDatePresets: SERVICE_CYCLE_PRESETS,
            },
            colSpan: 4,
        },
        {
            formItemProps: {
                name: 'renew_range',
                type: 'number',
                label: t('renew_range'),
            },
            colSpan: 2,
        },
        {
            renderEditComponent: () => (
                <div className="relative">
                    <span className="absolute -top-2.5 left-2 px-1 text-[11px] z-10 pointer-events-none bg-background text-muted-foreground">
                        {tService('renew_range_estimate')}
                    </span>
                    <div className="flex h-9 w-full items-center rounded-sm border border-input bg-transparent px-3 text-sm text-muted-foreground">
                        {renewEstimate.isValid()
                            ? renewEstimate.format('DD/MM/YYYY')
                            : '-'}
                    </div>
                </div>
            ),
            colSpan: 2,
        },
        {
            formItemProps: {
                name: 'payment_term_days',
                type: 'number',
                formatted: false,
                min: 0,
                max: MAX_PAYMENT_TERM_DAYS,
                step: 1,
                label: tService('payment_term_days'),
                helperText: tService('payment_term_days_hint'),
            },
            colSpan: 2,
        },
        ...(isReconcileScoped
            ? [
                  {
                      renderEditComponent: () => (
                          <div className="space-y-1">
                              <span className="text-xs font-medium text-gray-700">
                                  Khách hàng nguồn số liệu đối soát
                              </span>
                              {/* Chỉ render select khi field đã có value (đã bơm ở
                                  effect trên). Lúc này CustomerSelect mount với value
                                  sẵn có → AsyncSelectWithServer resolve đúng nhãn KH. */}
                              {isBillingMetaLoading ||
                              billingOrgValue == null ? (
                                  <div className="flex h-9 w-full items-center rounded-sm border border-input bg-transparent px-3 text-sm text-muted-foreground">
                                      Đang tải...
                                  </div>
                              ) : (
                                  <CustomerSelect
                                      key={String(billingOrgValue)}
                                      control={formControl.control as never}
                                      name={'billing_organization_id' as never}
                                      showLabel={false}
                                  />
                              )}
                              <p className="text-[11px] text-muted-foreground">
                                  Hệ thống dùng ID của khách hàng được chọn để
                                  lấy số liệu usage khi đối soát CDN. Mặc định
                                  là khách hàng của dịch vụ; chỉ đổi cho trường
                                  hợp số liệu nằm dưới khách hàng khác (vd
                                  reseller/đại lý).
                              </p>
                          </div>
                      ),
                      colSpan: 4 as const,
                  },
                  {
                      renderEditComponent: () => (
                          <div className="space-y-1">
                              <span className="text-xs font-medium text-gray-700">
                                  Dịch vụ (@mgmt) để lấy số liệu
                              </span>
                              <select
                                  className="flex h-9 w-full items-center rounded-sm border border-input bg-transparent px-3 text-sm"
                                  value={assignedServiceId}
                                  onChange={(e) =>
                                      setAssignedServiceId(e.target.value)
                                  }
                              >
                                  <option value="">
                                      Toàn bộ Org (mặc định)
                                  </option>
                                  {/* Giá trị đang gán nhưng không có trong list org →
                                      vẫn hiện để user thấy (đánh dấu lệch). */}
                                  {assignmentOrphaned && (
                                      <option value={savedAssignedServiceId}>
                                          {savedAssignedServiceId} (không thuộc
                                          org)
                                      </option>
                                  )}
                                  {(assignmentOptions || []).map((s) => (
                                      <option key={s.id} value={s.id}>
                                          {s.name}
                                      </option>
                                  ))}
                              </select>
                              {assignmentOrphaned && (
                                  <div className="rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                                      ⚠ creatable_uuid đang gán (
                                      {savedAssignedServiceId}) KHÔNG nằm trong
                                      danh sách dịch vụ CDN của org. Có thể do
                                      đổi org nguồn số liệu, hoặc dịch vụ @mgmt
                                      đã bị xoá/di chuyển. Monitoring/đối soát
                                      có thể ra 0 — chọn lại dịch vụ đúng hoặc
                                      để &quot;Toàn bộ Org&quot;.
                                  </div>
                              )}
                              <p className="text-[11px] text-muted-foreground">
                                  Với KH có nhiều dịch vụ CDN chung 1 org (vd
                                  CMC: VTV / VTVCDS / VOV), chọn đúng dịch vụ
                                  @mgmt để monitoring & đối soát lấy số của
                                  riêng dịch vụ này. Để trống = lấy tổng cả org.
                              </p>
                          </div>
                      ),
                      colSpan: 4 as const,
                  },
              ]
            : []),
    ]

    return (
        <Dialog open={open} onClose={onClose} title={t('service-edit')}>
            <DatacentralHookForm
                formControl={formControl}
                data={formData}
                onSubmit={onSubmit}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outline',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('submit'),
                        type: 'submit',
                        variant: 'default',
                        disabled: isPending,
                        loading: isPending,
                    },
                ]}
            />
        </Dialog>
    )
}

export default ServiceEdit
